import React from "react";
import Reveal from "./Reveal";
import { Layers, Ruler, Timer, ShieldCheck } from "lucide-react";

const MIVAN_IMG =
  "https://images.unsplash.com/photo-1764397301085-516f4d5bfdc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwcGlwaW5nJTIwZmFjdG9yeXxlbnwwfHx8fDE3Nzg2NTgyNDB8MA&ixlib=rb-4.1.0&q=85";

const advantages = [
  { icon: Ruler, title: "Precision sleeve & chase planning", body: "Every penetration is coordinated against the aluminium shutter layout before the pour." },
  { icon: Timer, title: "Faster cycle compatibility", body: "Plumbing teams move in lock-step with Mivan cycles to keep slab-to-slab schedules intact." },
  { icon: Layers, title: "Reduced post-pour rework", body: "Embedded conduits and pre-fixed inserts eliminate chiselling of structural walls." },
  { icon: ShieldCheck, title: "Code-compliant integrity", body: "Hydrostatic testing and joint protocols protect the structural concrete envelope." },
];

export const MivanTech = () => {
  return (
    <section id="mivan" data-testid="mivan-section" className="relative py-28 md:py-40 bg-bone-50 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="col-span-12 lg:col-span-6">
            <Reveal>
              <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-gold-deep">
                / 03 — Mivan Technology
              </span>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">
                Engineered for <span className="italic text-gold-deep">aluminium-form</span> construction.
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-8 text-base md:text-lg text-slate-700 leading-relaxed">
                Mivan formwork has redrawn the economics of modern high-rise construction in
                India — but it leaves no room for improvisation. Plumbing must be planned,
                drawn, sleeved and signed-off before a single panel is set.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="mt-5 text-base text-slate-700 leading-relaxed">
                P. Jasvantrai &amp; Co. has developed a dedicated Mivan coordination practice
                — one that reads structural shop drawings, harmonises with MEP consultants,
                and delivers a plumbing package that survives every slab cycle without
                surprise.
              </p>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-900/8 border border-slate-900/8">
              {advantages.map((a, i) => {
                const Icon = a.icon;
                return (
                  <Reveal
                    key={a.title}
                    delay={i * 100}
                    className="bg-white p-6 hover:bg-bone-100 transition-colors duration-300"
                    data-testid={`mivan-advantage-${i}`}
                  >
                    <Icon size={22} strokeWidth={1.25} className="text-gold-deep" />
                    <h4 className="mt-5 font-display text-xl text-slate-900 leading-tight">
                      {a.title}
                    </h4>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                      {a.body}
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <Reveal delay={150} className="relative blueprint-border overflow-hidden">
              <img
                src={MIVAN_IMG}
                alt="Industrial piping infrastructure"
                className="w-full h-[480px] md:h-[640px] object-cover grayscale-[0.3]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-transparent to-transparent" />

              {/* Spec tag */}
              <div className="absolute left-6 bottom-6 right-6 flex items-end justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-gold">
                    Plate · M-07
                  </div>
                  <div className="mt-2 font-display text-2xl md:text-3xl text-white leading-tight max-w-xs">
                    Coordinated sleeves &amp; risers ahead of every Mivan pour cycle.
                  </div>
                </div>
                <div className="hidden md:block font-mono text-[10px] uppercase tracking-[0.28em] text-steel-light">
                  Sec. 03.4
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MivanTech;
